import React, { useState, useEffect } from "react";

const EditStep = ({
  csvData,
  validationErrors,
  handleUpdateCsvData,
  uploadType,
}) => {
  const [editedData, setEditedData] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setEditedData(csvData);
    setErrors(validationErrors);
  }, [csvData, validationErrors]);

  const totalErrorRows = errors.filter(
    (error) => error && error.errors.length > 0
  ).length;

  const formatKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  const getExpectedFormat = () => {
    switch (uploadType) {
      case "eventCreate":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Event Creation:</p>
            <ul>
              <li>event_name: SCORE, HOTS, Math Olympiad, or I-EMSAT</li>
              <li>start_date: dd-mm-yyyy</li>
              <li>end_date: dd-mm-yyyy</li>
              <li>start_time: hh:mm AM/PM</li>
              <li>end_time: hh:mm AM/PM</li>
              <li>event_level: Prelims, Semi-Finals, or Finals</li>
              <li>event_round: Round X (where X is a number)</li>
              <li>school_id: Valid school ID format</li>
            </ul>
          </div>
        );
      case "eventReschedule":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Event Rescheduling:</p>
            <ul>
              <li>event_name: Valid event name (e.g., SCORE, HOTS)</li>
              <li>school_id: Valid school ID format</li>
              <li>current_start_date_time: YYYY-MM-DDTHH:mm</li>
              <li>current_end_date_time: YYYY-MM-DDTHH:mm</li>
              <li>reschedule_start_date_time: YYYY-MM-DDTHH:mm</li>
              <li>reschedule_end_date_time: YYYY-MM-DDTHH:mm</li>
              <li>level: Prelims, Semi-Finals, or Finals</li>
              <li>round: Positive integer (e.g., 1, 2, 3)</li>
            </ul>
          </div>
        );
      case "eventDelete":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Event Deletion:</p>
            <ul>
              <li>school_id: Valid school ID format</li>
              <li>old_start_date: dd-mm-yyyy</li>
              <li>old_end_date: dd-mm-yyyy</li>
              <li>start_time: hh:mm AM/PM</li>
              <li>end_time: hh:mm AM/PM</li>
            </ul>
          </div>
        );
      case "studentRegister":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Student Registration:</p>
            <ul>
              <li>isd_code: Valid ISD code</li>
              <li>first_name: Letters only</li>
              <li>last_name: Letters only (optional)</li>
              <li>
                phone: For India (ISD 91): 10 digits, For UAE (ISD 971): 7
                digits starting with 5 or higher
              </li>
              <li>grade: 1-21</li>
              <li>school_id: Valid school ID format</li>
            </ul>
          </div>
        );
      case "uploadOfflineResponses":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Offline Responses:</p>
            <ul>
              <li>first_name: Letters only</li>
              <li>last_name: Letters only (optional)</li>
              <li>isd_code: Valid ISD code</li>
              <li>
                phone: For India (ISD 91): 10 digits, For UAE (ISD 971): 9
                digits starting with 5 or higher
              </li>
              <li>grade: 1-21</li>
              <li>school_id: Valid school ID format</li>
              <li>
                admission_id: Alphanumeric, 6-9 characters (must be unique
                across all rows)
              </li>
              <li>
                event_name: Valid event name (e.g., SCORE, HOTS, Math Olympiad,
                I-EMSAT)
              </li>
              <li>level: Prelims, Semi-Finals, or Finals</li>
              <li>round: Round X (where X is a number)</li>
              <li>exam_name: Name of the exam (e.g., NEET)</li>
              <li>
                Q1, Q2, Q3, Q4,...: Answer values (must be 1, 2, 3, or 4 only)
              </li>
            </ul>
          </div>
        );
      case "globalStudentsRegister":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Global Student Registration:</p>
            <ul>
              <li>isd_code: Valid ISD code</li>
              <li>first_name: Letters only</li>
              <li>last_name: Letters only (optional)</li>
              <li>
                phone: For India (ISD 91): 10 digits, For UAE (ISD 971): 7
                digits starting with 5 or higher
              </li>
              <li>grade: 1-21</li>
              <li>school_id: Valid school ID format</li>
              <li>admission_id: Alphanumeric</li>
            </ul>
          </div>
        );
      default:
        return null;
    }
  };

  const isIncorrectFormat = errors.some((error) => {
    const expectedKeys =
      {
        eventCreate: [
          "event_name",
          "start_date",
          "end_date",
          "start_time",
          "end_time",
          "event_level",
          "event_round",
          "school_id",
        ],
        eventReschedule: [
          "event_name",
          "school_id",
          "current_start_date_time",
          "current_end_date_time",
          "reschedule_start_date_time",
          "reschedule_end_date_time",
          "level",
          "round",
        ],
        eventDelete: [
          "school_id",
          "old_start_date",
          "old_end_date",
          "start_time",
          "end_time",
        ],
        studentRegister: [
          "isd_code",
          "first_name",
          "last_name",
          "phone",
          "grade",
          "school_id",
        ],
        uploadOfflineResponses: [
          "first_name",
          "last_name",
          "isd_code",
          "phone",
          "grade",
          "school_id",
          "admission_id",
          "event_name",
          "level",
          "round",
          "exam_name",
        ],
        globalStudentsRegister: [
          "isd_code",
          "first_name",
          "last_name",
          "phone",
          "grade",
          "school_id",
          "admission_id",
        ],
      }[uploadType] || [];

    return !error.data || !expectedKeys.every((key) => key in error.data);
  });

  return (
    <div className="editcontianer">
      <h3>Total Error Rows: {totalErrorRows}</h3>

      {isIncorrectFormat && (
        <div className="format-error">
          <h4>⚠️ Incorrect CSV Format Detected</h4>
          <p>
            The uploaded CSV does not match the expected format for{" "}
            {uploadType.replace(/([A-Z])/g, " $1").toLowerCase()}.
          </p>
          {getExpectedFormat()}
          <div className="uploaded-data">
            <p>Your uploaded data:</p>
            <pre>{JSON.stringify(errors[0]?.data || {}, null, 2)}</pre>
          </div>
        </div>
      )}

      {!isIncorrectFormat && (
        <>
          <div
            className="editstep-table-wrapper"
            role="region"
            aria-live="polite"
          >
            <table>
              <thead>
                <tr>
                  {uploadType === "eventCreate" && (
                    <>
                      <th>Event Name</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Event Level</th>
                      <th>Event Round</th>
                      <th>Reason of Failure</th>
                    </>
                  )}
                  {uploadType === "eventReschedule" && (
                    <>
                      <th>School ID</th>
                      <th>Event Name</th>
                      <th>Current Start Date Time</th>
                      <th>Current End Date Time</th>
                      <th>Reschedule Start Date Time</th>
                      <th>Reschedule End Date Time</th>
                      <th>Level</th>
                      <th>Round</th>
                      <th>Reason of Failure</th>
                    </>
                  )}
                  {uploadType === "eventDelete" && (
                    <>
                      <th>School ID</th>
                      <th>Old Start Date</th>
                      <th>Old End Date</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Reason of Failure</th>
                    </>
                  )}
                  {uploadType === "studentRegister" && (
                    <>
                      <th>ISD Code</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Phone</th>
                      <th>Grade</th>
                      <th>School ID</th>
                      <th>Reason of Failure</th>
                    </>
                  )}
                  {uploadType === "uploadOfflineResponses" && (
                    <>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>ISD Code</th>
                      <th>Phone</th>
                      <th>Grade</th>
                      <th>School ID</th>
                      <th>Admission ID</th>
                      <th>Event Name</th>
                      <th>Level</th>
                      <th>Round</th>
                      <th>Exam Name</th>
                      {errors[0]?.data &&
                        Object.keys(errors[0].data)
                          .filter((key) => key.match(/^Q\d+$/))
                          .sort((a, b) => {
                            const aNum = parseInt(a.slice(1));
                            const bNum = parseInt(b.slice(1));
                            return aNum - bNum;
                          })
                          .map((key) => <th key={key}>{key}</th>)}
                      <th>Reason of Failure</th>
                    </>
                  )}
                  {uploadType === "globalStudentsRegister" && (
                    <>
                      <th>ISD Code</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Phone</th>
                      <th>Grade</th>
                      <th>School ID</th>
                      <th>Admission ID</th>
                      <th>Reason of Failure</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {errors.map((error, index) => (
                  <tr className="trcontainer" key={index}>
                    {uploadType === "eventCreate" && (
                      <>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("event name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.event_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("start date")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.start_date}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("end date")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.end_date}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("start time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.start_time}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("end time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.end_time}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("event level")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.event_level}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("event round")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.event_round}
                        </td>
                        <td className="error-messages">
                          {error.errors.join(",\n")}
                        </td>
                      </>
                    )}
                    {uploadType === "eventReschedule" && (
                      <>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("school id")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.school_id}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("event name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.event_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err
                                .toLowerCase()
                                .includes("current start date time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.current_start_date_time}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err
                                .toLowerCase()
                                .includes("current end date time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.current_end_date_time}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err
                                .toLowerCase()
                                .includes("reschedule start date time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.reschedule_start_date_time}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err
                                .toLowerCase()
                                .includes("reschedule end date time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.reschedule_end_date_time}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("level")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.level}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("round")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.round}
                        </td>
                        <td className="error-messages">
                          {error.errors.join(",\n")}
                        </td>
                      </>
                    )}
                    {uploadType === "eventDelete" && (
                      <>
                        <td>{error?.data?.school_id}</td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("start date")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.old_start_date}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("end date")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.old_end_date}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("start time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.start_time}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("end time")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.end_time}
                        </td>
                        <td className="error-messages">
                          {error.errors.join(",\n")}
                        </td>
                      </>
                    )}
                    {uploadType === "studentRegister" && (
                      <>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("isd code")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.isd_code}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("first name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.first_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("last name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.last_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("phone")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.phone}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("grade")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.grade}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("school")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.school_id}
                        </td>
                        <td className="error-messages">
                          {error.errors.join(",\n")}
                        </td>
                      </>
                    )}
                    {uploadType === "globalStudentsRegister" && (
                      <>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("isd code")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.isd_code}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("first name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.first_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("last name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.last_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("phone")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.phone}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("grade")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.grade}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("school")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.school_id}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("admission")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.admission_id}
                        </td>
                        <td className="error-messages">
                          {error.errors.join(",\n")}
                        </td>
                      </>
                    )}
                    {uploadType === "uploadOfflineResponses" && (
                      <>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("first name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.first_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("last name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.last_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("isd code")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.isd_code}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("phone")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.phone}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("grade")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.grade}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("school")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.school_id}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("admission")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.admission_id}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("event name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.event_name}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("level")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.level}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("round")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.round}
                        </td>
                        <td
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes("exam name")
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data?.exam_name}
                        </td>
                        {Object.keys(error?.data || {})
                          .filter((key) => key.match(/^Q\d+$/))
                          .sort((a, b) => {
                            const aNum = parseInt(a.slice(1));
                            const bNum = parseInt(b.slice(1));
                            return aNum - bNum;
                          })
                          .map((key) => (
                            <td
                              key={key}
                              className={
                                error.errors.some((err) =>
                                  err.toLowerCase().includes(key.toLowerCase())
                                )
                                  ? "errorMessage"
                                  : ""
                              }
                            >
                              {error?.data[key]}
                            </td>
                          ))}
                        <td className="error-messages">
                          {error.errors.join(",\n")}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="editstep-card-list">
            {errors.map((error, index) => (
              <article className="editstep-card" key={`card-${index}`}>
                <header className="editstep-card__header">
                  <span className="editstep-card__row-label">
                    Row{" "}
                    {typeof error.index === "number"
                      ? error.index + 1
                      : index + 1}
                  </span>
                </header>
                <dl className="editstep-card__details">
                  {Object.entries(error?.data || {}).map(([key, value]) => (
                    <div className="editstep-card__detail" key={key}>
                      <dt>{formatKey(key)}</dt>
                      <dd>
                        {value !== undefined && value !== "" ? value : "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="editstep-card__errors">
                  <h4>Issues</h4>
                  <ul>
                    {error.errors.map((err, errIndex) => (
                      <li key={errIndex}>{err}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EditStep;
